import { useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { FounderLevel, LevelProgress, Milestone } from "@/types/levels";

import {
  Badge,
  BookmarkStar,
  Crown,
  Lock,
  TickCircle,
} from "../../../components/icons";
import {
  getCurrentRevenue,
  getMilestonesForLevel,
  getRevenueThreshold,
} from "./stack-levels/utils";

interface StackLevelsWidgetProps {
  levelProgress: LevelProgress;
  isLoading?: boolean;
  hideStatusBenefitsLink?: boolean;
  /**
   * Force the widget to show a specific level instead of inferring from currentLevel
   */
  initialLevel?: FounderLevel;
  /**
   * If true, the widget will behave as unlocked (no blur) even if locked
   */
  forceUnlocked?: boolean;
}

const StackLevelsWidget = ({
  levelProgress,
  isLoading,
  hideStatusBenefitsLink = false,
  initialLevel,
  forceUnlocked = false,
}: StackLevelsWidgetProps) => {
  const [viewingLevel, setViewingLevel] = useState<FounderLevel>(
    initialLevel || levelProgress.currentLevel || FounderLevel.STACK_CERTIFIED,
  );

  const levels = [
    FounderLevel.STACK_CERTIFIED,
    FounderLevel.STACK_PRO,
    FounderLevel.STACK_X,
  ];
  const currentLevelIndex = levels.indexOf(viewingLevel);

  const navigateLevel = (direction: "prev" | "next") => {
    const newIndex =
      direction === "prev" ? currentLevelIndex - 1 : currentLevelIndex + 1;
    if (newIndex >= 0 && newIndex < levels.length) {
      setViewingLevel(levels[newIndex]);
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full bg-[#f9f9f9] rounded-2xl">
        <CardContent className="p-6 h-[150px] flex flex-col">
          <div className="flex items-end gap-2 mb-4">
            <div className="h-6 w-28 rounded bg-[#efefef] animate-pulse" />
            <div className="h-6 w-20 rounded bg-[#efefef] animate-pulse" />
          </div>
          <div className="mt-auto">
            <div className="relative h-2 w-full rounded-[8px] bg-[#e8e8e8] overflow-hidden">
              <div className="absolute left-0 top-0 h-2 w-4 rounded-[8px] bg-[#d1d1d2]" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const milestonesForLevel = getMilestonesForLevel(
    viewingLevel,
    levelProgress.milestones,
  );
  const isCurrentLevel = viewingLevel === levelProgress.currentLevel;
  const revenueThreshold = getRevenueThreshold(viewingLevel);
  const currentRevenue = getCurrentRevenue(levelProgress.milestones);

  const upcomingLevel = levelProgress.nextLevel || FounderLevel.STACK_CERTIFIED;
  const isUpcoming = viewingLevel === upcomingLevel;

  const levelBadgeStyles = (() => {
    if (viewingLevel === FounderLevel.STACK_CERTIFIED)
      return {
        text: "Stack Certified",
        className: "bg-[#dcddfa] text-[#4f55e8]",
      };
    if (viewingLevel === FounderLevel.STACK_PRO)
      return { text: "Stack Pro", className: "bg-[#def5f9] text-[#3eacbc]" };
    return { text: "Stack X", className: "bg-[#e6f0cc] text-[#83b300]" };
  })();

  const completedCount = milestonesForLevel.filter((m) => m.completed).length;
  const totalCount = milestonesForLevel.length || 1;
  const percent = Math.min(
    100,
    Math.round((completedCount / totalCount) * 100),
  );
  const revenuePercent =
    revenueThreshold > 0
      ? Math.min(100, Math.round((currentRevenue / revenueThreshold) * 100))
      : 0;

  const getUpcomingBadgeColor = () => {
    if (viewingLevel === FounderLevel.STACK_CERTIFIED) return "#4f55e8";
    if (viewingLevel === FounderLevel.STACK_PRO) return "#3eacbc";
    return "#83b300";
  };

  const renderLevelIcon = () => {
    if (viewingLevel === FounderLevel.STACK_CERTIFIED) {
      return <Badge className="ml-1 h-4 w-4 text-[#4f55e8]" />;
    }
    if (viewingLevel === FounderLevel.STACK_PRO) {
      return <BookmarkStar className="ml-1 h-4 w-4 text-[#3eacbc]" />;
    }
    return <Crown className="ml-1 h-4 w-4 text-[#83b300]" />;
  };

  const shouldBlurContent = viewingLevel !== upcomingLevel;

  const Row = ({ milestone }: { milestone: Milestone }) => {
    const isPartial =
      !!milestone.total &&
      !!milestone.progress &&
      milestone.progress > 0 &&
      milestone.progress < (milestone.total || 0);
    return (
      <div className="flex items-center justify-start">
        <div className="flex items-center gap-2">
          {milestone.completed ? (
            <TickCircle
              className={`h-[18px] w-[18px] text-[#767677] ${
                shouldBlurContent ? "filter blur-[5px]" : ""
              }`}
            />
          ) : isPartial ? (
            <span
              className={`relative inline-flex items-center justify-center size-[18px] rounded-full border border-[#d1d1d2] ${
                shouldBlurContent ? "filter blur-[5px]" : ""
              }`}
            >
              <span className="absolute left-[3px] right-[3px] h-[4px] bg-[#494949] rounded-[8px]" />
            </span>
          ) : (
            <span
              className={`inline-block size-[18px] rounded-full border border-[#e8e8e8] border-dashed ${
                shouldBlurContent ? "filter blur-[5px]" : ""
              }`}
            />
          )}
          <span
            className={`text-[14px] font-semibold tracking-[-0.56px] text-[#767677] ${
              shouldBlurContent ? "filter blur-[5px]" : ""
            }`}
          >
            {milestone.title}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Card className={`w-full bg-[#f9f9f9] rounded-2xl`}>
      <CardContent className="p-6 h-full flex flex-col">
        {/* Header badges + lock when blurred */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-end gap-2">
            {isUpcoming && (
              <span
                className="h-6 inline-flex items-center rounded px-[6px] py-[4px] text-xs font-semibold"
                style={{
                  border: `1px solid ${getUpcomingBadgeColor()}`,
                  color: getUpcomingBadgeColor(),
                }}
              >
                Your upcoming level
              </span>
            )}
            <span
              className={`h-6 inline-flex items-center rounded px-[6px] py-[4px] text-xs font-semibold ${levelBadgeStyles.className}`}
            >
              {levelBadgeStyles.text}
              {renderLevelIcon()}
            </span>
          </div>
          {shouldBlurContent && <Lock className="h-5 w-5 text-[#494949]" />}
        </div>

        {/* Milestones list */}
        <div className="space-y-3">
          {milestonesForLevel.slice(0, 4).map((m) => (
            <Row key={m.id} milestone={m} />
          ))}
        </div>

        {/* Progress bar (hidden for Level 1/when no revenue threshold) */}
        {revenueThreshold > 0 && (
          <div
            className={`mt-auto pt-4 flex items-center gap-2 ${
              shouldBlurContent ? "filter blur-[5px]" : ""
            }`}
          >
            <div className="relative h-2 w-full rounded-[8px] bg-[#e8e8e8] overflow-hidden">
              <div
                className="absolute left-0 top-0 h-2 rounded-[8px] bg-[#494949]"
                style={{ width: `${Math.max(4, revenuePercent)}%` }}
              />
            </div>
            <span className="text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
              $
              {new Intl.NumberFormat("en-US").format(
                Math.floor(currentRevenue),
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StackLevelsWidget;
